import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { fetchWithCorpId, fetchWithAuth } from '../utils/api';

const countFrom = (data) => {
  if (!data) return '—';
  if (Array.isArray(data)) return data.length;
  if (data.totalElements !== undefined) return data.totalElements;
  if (data.content && Array.isArray(data.content)) return data.content.length;
  return '—';
};

const toArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.content && Array.isArray(data.content)) return data.content;
  return [];
};

const safe = (fn) => fn().catch(() => null);

const ROLE_META = {
  super:    { label: 'Super Administrator', color: '#6f4a8c', bg: '#f3edf5', border: '#dcc9e2' },
  admin:    { label: 'Administrator',       color: '#b08a5a', bg: '#f1ebda', border: '#e0d2b5' },
  employer: { label: 'Employer',            color: '#4f9d62', bg: '#eef7ef', border: '#cce4d1' },
  employee: { label: 'Employee',            color: '#b07a14', bg: '#fbf3df', border: '#ecd9a8' },
};

/* Stat-card accents — re-anchored on the Claude trinity:
   coral primary, dark ink, accent teal/amber, with warm
   muted tones rather than the prior cool palette. */
const ACCENTS = {
  corp:      '#181715',  /* surface-dark */
  client:    '#b08a5a',  /* camel */
  employee:  '#4f9d62',  /* warm green */
  admin:     '#8a6c45',  /* camel-active */
  timesheet: '#5db8a6',  /* accent-teal */
  invoice:   '#e8a55a',  /* accent-amber */
  order:   '#6f4a8c',  /* warm violet */
  pending:   '#b07a14',  /* warm amber */
  approved:  '#4f9d62',  /* warm green */
  danger:    '#c64545',  /* warm red */
};

const StatCard = ({ label, value, icon, accentColor, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: 'var(--canvas)',
      border: '1px solid var(--hairline)',
      borderRadius: 'var(--radius-lg)',
      padding: '14px 16px',
      display: 'flex', alignItems: 'center', gap: '12px',
      boxShadow: 'none',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'background 0.18s, border-color 0.18s',
      flex: '1 1 160px',
      minWidth: '150px',
    }}
    onMouseOver={e => { if (onClick) { e.currentTarget.style.background = 'var(--surface-soft)'; e.currentTarget.style.borderColor = 'var(--surface-cream-strong)'; } }}
    onMouseOut={e => { e.currentTarget.style.background = 'var(--canvas)'; e.currentTarget.style.borderColor = 'var(--hairline)'; }}
  >
    <div style={{
      width: '34px', height: '34px', borderRadius: 'var(--radius-md)',
      background: 'var(--surface-soft)', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ color: accentColor, display: 'flex', transform: 'scale(0.85)' }}>{icon}</span>
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{
        fontFamily: 'var(--font-sans)',
        fontSize: '11px', color: 'var(--muted)', fontWeight: 500,
        letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '2px',
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: 'var(--font-serif)',
        fontSize: '24px', fontWeight: 500, color: 'var(--ink)',
        lineHeight: 1, letterSpacing: '-0.5px',
      }}>
        {value === null ? (
          <span style={{ display: 'inline-block', width: '36px', height: '18px', background: 'var(--surface-cream-strong)', borderRadius: '4px', animation: 'pulse 1.5s ease infinite' }} />
        ) : value}
      </div>
    </div>
  </div>
);

const ActionBtn = ({ label, path, color, icon }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        height: '40px', padding: '0 18px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--hairline)',
        background: 'var(--canvas)',
        color: 'var(--ink)', cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
        fontSize: '14px', fontWeight: 500,
        transition: 'background 0.16s, border-color 0.16s',
      }}
      onMouseOver={e => { e.currentTarget.style.background = 'var(--surface-soft)'; e.currentTarget.style.borderColor = 'var(--surface-cream-strong)'; }}
      onMouseOut={e => { e.currentTarget.style.background = 'var(--canvas)'; e.currentTarget.style.borderColor = 'var(--hairline)'; }}
    >
      <span style={{ color: color, display: 'flex' }}>{icon}</span>
      {label}
    </button>
  );
};

const SectionLabel = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '8px 0 12px' }}>
    <span style={{
      fontFamily: 'var(--font-sans)',
      fontSize: '11px', fontWeight: 500, color: 'var(--muted)',
      letterSpacing: '1.4px', textTransform: 'uppercase',
    }}>{text}</span>
    <div style={{ flex: 1, height: '1px', background: 'var(--hairline)' }} />
  </div>
);

/* ───── Cream card wrapper used by every widget ─────────── */
const Card = ({ title, subtitle, children, action, style }) => (
  <div style={{
    background: 'var(--canvas)',
    border: '1px solid var(--hairline)',
    borderRadius: 'var(--radius-lg)',
    padding: '18px 20px',
    display: 'flex', flexDirection: 'column', gap: '14px',
    minWidth: 0,
    ...style,
  }}>
    {(title || action) && (
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' }}>
        <div style={{ minWidth: 0 }}>
          {title && (
            <div style={{
              fontFamily: 'var(--font-sans)', fontSize: '11px', fontWeight: 500,
              color: 'var(--muted)', letterSpacing: '1.5px', textTransform: 'uppercase',
            }}>{title}</div>
          )}
          {subtitle && (
            <div style={{
              marginTop: '4px',
              fontFamily: 'var(--font-sans)', fontSize: '13px', color: 'var(--body-text)',
            }}>{subtitle}</div>
          )}
        </div>
        {action}
      </div>
    )}
    {children}
  </div>
);

/* ───── Chart primitives (hand-rolled SVG, no deps) ────── */

const CHART_COLORS = {
  primary: '#b08a5a',      // camel
  primarySoft: 'rgba(176,138,90,0.14)',
  teal: '#5db8a6',
  amber: '#e8a55a',
  plum: '#8c5a73',
  ink: '#141413',
  muted: '#6c6a64',
  mutedSoft: '#8e8b82',
  hairline: '#e6dfd8',
  surfaceSoft: '#f5f0e8',
  success: '#4f9d62',
  warning: '#d4a017',
  danger: '#c64545',
};

// Smooth area chart for monthly revenue trend.
const AreaChart = ({ data, height = 150, formatY = (v) => v }) => {
  const W = 600, H = height;
  const padL = 44, padR = 12, padT = 16, padB = 32;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const max = Math.max(1, ...data.map(d => d.value));
  // Nice round y-axis ceiling.
  const ceil = Math.ceil(max / 5) * 5 || max;
  const yTicks = [0, ceil * 0.5, ceil];

  const xStep = data.length > 1 ? innerW / (data.length - 1) : 0;
  const xy = (i, v) => [padL + i * xStep, padT + innerH - (v / ceil) * innerH];

  const pts = data.map((d, i) => xy(i, d.value));
  const linePath = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${pts[pts.length - 1][0].toFixed(1)} ${padT + innerH} L ${pts[0][0].toFixed(1)} ${padT + innerH} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none" style={{ display: 'block' }}>
      {/* Y gridlines + labels */}
      {yTicks.map((t, i) => {
        const y = padT + innerH - (t / ceil) * innerH;
        return (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={y} y2={y} stroke={CHART_COLORS.hairline} strokeWidth="1" />
            <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="11" fill={CHART_COLORS.mutedSoft} fontFamily="Inter, sans-serif">
              {formatY(t)}
            </text>
          </g>
        );
      })}
      {/* Area */}
      <path d={areaPath} fill={CHART_COLORS.primarySoft} />
      <path d={linePath} fill="none" stroke={CHART_COLORS.primary} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {/* Points */}
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3.5" fill="#faf9f5" stroke={CHART_COLORS.primary} strokeWidth="2" />
      ))}
      {/* X labels */}
      {data.map((d, i) => {
        const [x] = xy(i, 0);
        return (
          <text key={i} x={x} y={H - 10} textAnchor="middle" fontSize="11" fill={CHART_COLORS.muted} fontFamily="Inter, sans-serif">
            {d.label}
          </text>
        );
      })}
    </svg>
  );
};

// Bar chart for weekly hours.
const BarChart = ({ data, height = 150, formatY = (v) => v }) => {
  const W = 600, H = height;
  const padL = 36, padR = 12, padT = 16, padB = 32;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const max = Math.max(1, ...data.map(d => d.value));
  const ceil = Math.ceil(max / 10) * 10 || max;
  const yTicks = [0, ceil * 0.5, ceil];
  const slot = innerW / data.length;
  const barW = Math.min(28, slot * 0.6);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none" style={{ display: 'block' }}>
      {yTicks.map((t, i) => {
        const y = padT + innerH - (t / ceil) * innerH;
        return (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={y} y2={y} stroke={CHART_COLORS.hairline} strokeWidth="1" />
            <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="11" fill={CHART_COLORS.mutedSoft} fontFamily="Inter, sans-serif">
              {formatY(t)}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const x = padL + i * slot + (slot - barW) / 2;
        const h = (d.value / ceil) * innerH;
        const y = padT + innerH - h;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={Math.max(2, h)} rx="3" fill={CHART_COLORS.primary} />
            <text x={x + barW / 2} y={H - 10} textAnchor="middle" fontSize="11" fill={CHART_COLORS.muted} fontFamily="Inter, sans-serif">
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// Compact donut: smaller circle, legend underneath.
const Donut = ({ slices, total, centerLabel }) => {
  const size = 130, r = 50, stroke = 16;
  const cx = size / 2, cy = size / 2;
  const C = 2 * Math.PI * r;
  const sum = slices.reduce((a, s) => a + s.value, 0) || 1;
  let offset = 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={CHART_COLORS.surfaceSoft} strokeWidth={stroke} />
        {slices.map((s, i) => {
          const len = (s.value / sum) * C;
          const dash = `${len} ${C - len}`;
          const el = (
            <circle key={i} cx={cx} cy={cy} r={r} fill="none"
              stroke={s.color} strokeWidth={stroke}
              strokeDasharray={dash}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${cx} ${cy})`}
              strokeLinecap="butt"
            />
          );
          offset += len;
          return el;
        })}
        <text x={cx} y={cy - 2} textAnchor="middle"
          fontFamily="Cormorant Garamond, serif" fontSize="26" fontWeight="500"
          fill={CHART_COLORS.ink} letterSpacing="-0.5">
          {total}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle"
          fontFamily="Inter, sans-serif" fontSize="10" fill={CHART_COLORS.muted}
          letterSpacing="1.4" textTransform="uppercase">
          {centerLabel}
        </text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
        {slices.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: '12px', color: 'var(--body-text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.label}
            </span>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: '12px', fontWeight: 500, color: 'var(--ink)' }}>
              {s.value}
            </span>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: '11px', color: 'var(--muted-soft)', minWidth: '28px', textAlign: 'right' }}>
              {Math.round((s.value / sum) * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ───── Derivation helpers — turn raw lists into chart data ── */

const monthBuckets = (count, ref = new Date()) => {
  const arr = [];
  const d = new Date(ref);
  d.setUTCDate(1);
  for (let i = count - 1; i >= 0; i--) {
    const m = new Date(d);
    m.setUTCMonth(m.getUTCMonth() - i);
    arr.push({
      key: `${m.getUTCFullYear()}-${String(m.getUTCMonth() + 1).padStart(2, '0')}`,
      label: m.toLocaleString('en-US', { month: 'short' }),
      value: 0,
    });
  }
  return arr;
};

const weekBuckets = (count, ref = new Date()) => {
  const arr = [];
  const d = new Date(ref);
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - d.getUTCDay()); // Sunday
  for (let i = count - 1; i >= 0; i--) {
    const w = new Date(d);
    w.setUTCDate(w.getUTCDate() - i * 7);
    arr.push({
      key: w.toISOString().slice(0, 10),
      label: `${w.getUTCMonth() + 1}/${w.getUTCDate()}`,
      value: 0,
    });
  }
  return arr;
};

const fmtMoney = (v) =>
  v >= 1000 ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `$${Math.round(v)}`;

// Icons
const Icons = {
  corp: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  client: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  employee: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  invoice: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  order: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  timesheet: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  admin: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  plus: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  check: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
  clock: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
};

const Dashboard = () => {
  const { getUserRoles, isSuperUser, token } = useAuth();
  const navigate = useNavigate();
  const roles = getUserRoles();
  const isSuper = isSuperUser();
  const isAdmin = roles.includes('admin');
  const isEmployer = roles.includes('employer');
  const isEmployee = roles.includes('employee');

  const primaryRole = isSuper ? 'super' : isAdmin ? 'admin' : isEmployer ? 'employer' : 'employee';
  const roleMeta = ROLE_META[primaryRole];

  const parseJwt = (t) => { try { return JSON.parse(atob(t.split('.')[1])); } catch { return null; } };
  const tokenData = token ? parseJwt(token) : null;
  const username = tokenData?.sub || tokenData?.given_name || 'User';
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const [stats, setStats] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      const s = {};
      // Always pull invoices / timesheets / clients with a large page so we
      // can derive trend / status / top-N charts client-side. The list
      // endpoints already accept ?size=99999 (see ClientTable + form pickers).
      const bigPage = 'page=0&size=99999';
      if (isSuper) {
        const [corps, clientsRes, employees, admins, invRes, tsRes] = await Promise.all([
          safe(() => fetchWithAuth('/corps')),
          safe(() => fetchWithCorpId(`/clients?${bigPage}&sort=name`)),
          safe(() => fetchWithCorpId('/employees')),
          safe(() => fetchWithCorpId('/admin')),
          safe(() => fetchWithCorpId(`/invoices?${bigPage}&sort=issueDate&direction=desc`)),
          safe(() => fetchWithCorpId(`/timesheets?${bigPage}&sort=periodStart&direction=desc`)),
        ]);
        s.corps     = corps     !== null ? countFrom(corps)     : '—';
        s.clients   = clientsRes !== null ? countFrom(clientsRes) : '—';
        s.employees = employees !== null ? countFrom(employees) : '—';
        s.admins    = admins    !== null ? countFrom(admins)    : '—';
        setInvoices(toArray(invRes));
        setTimesheets(toArray(tsRes));
      } else if (isAdmin) {
        const [clientsRes, employees, tsRes, invRes, orders] = await Promise.all([
          safe(() => fetchWithCorpId(`/clients?${bigPage}&sort=name`)),
          safe(() => fetchWithCorpId('/employees')),
          safe(() => fetchWithCorpId(`/timesheets?${bigPage}&sort=periodStart&direction=desc`)),
          safe(() => fetchWithCorpId(`/invoices?${bigPage}&sort=issueDate&direction=desc`)),
          safe(() => fetchWithCorpId('/orders')),
        ]);
        s.clients    = clientsRes !== null ? countFrom(clientsRes) : '—';
        s.employees  = employees  !== null ? countFrom(employees)  : '—';
        s.timesheets = tsRes      !== null ? countFrom(tsRes)      : '—';
        s.invoices   = invRes     !== null ? countFrom(invRes)     : '—';
        s.orders   = orders   !== null ? countFrom(orders)   : '—';
        setInvoices(toArray(invRes));
        setTimesheets(toArray(tsRes));
      } else if (isEmployer) {
        const [employees, invRes, tsRes] = await Promise.all([
          safe(() => fetchWithCorpId('/employees')),
          safe(() => fetchWithCorpId(`/invoices?${bigPage}&sort=issueDate&direction=desc`)),
          safe(() => fetchWithCorpId(`/timesheets?${bigPage}&sort=periodStart&direction=desc`)),
        ]);
        s.employees  = employees !== null ? countFrom(employees) : '—';
        s.timesheets = tsRes     !== null ? countFrom(tsRes)     : '—';
        const arr = toArray(invRes);
        s.invoices        = invRes !== null ? arr.length : '—';
        s.pendingInvoices = invRes !== null ? arr.filter(i => (i.status || '').toLowerCase() === 'pending').length : '—';
        setInvoices(arr);
        setTimesheets(toArray(tsRes));
      } else {
        const [tsRes] = await Promise.all([
          safe(() => fetchWithCorpId(`/timesheets?${bigPage}&sort=periodStart&direction=desc`)),
        ]);
        const arr = toArray(tsRes);
        s.timesheets         = tsRes !== null ? arr.length : '—';
        s.pendingTimesheets  = tsRes !== null ? arr.filter(t => (t.status || '').toLowerCase() === 'pending').length : '—';
        s.approvedTimesheets = tsRes !== null ? arr.filter(t => (t.status || '').toLowerCase() === 'approved').length : '—';
        setTimesheets(arr);
      }
      setStats(s);
      setLoaded(true);
    };
    load();
  }, [isSuper, isAdmin, isEmployer]);

  const v = (key) => loaded ? stats[key] ?? '—' : null;

  /* ── Derived chart data ─────────────────────────────── */

  const revenueSeries = useMemo(() => {
    const buckets = monthBuckets(6);
    invoices.forEach((inv) => {
      const key = (inv.issueDate || '').slice(0, 7);
      const b = buckets.find(b => b.key === key);
      if (b) b.value += Number(inv.amount) || 0;
    });
    return buckets;
  }, [invoices]);

  const invoiceStatusSlices = useMemo(() => {
    const tally = { pending: 0, paid: 0, overdue: 0 };
    invoices.forEach((inv) => {
      const k = (inv.status || '').toLowerCase();
      if (k in tally) tally[k] += 1;
    });
    return [
      { label: 'Paid',    value: tally.paid,    color: CHART_COLORS.success },
      { label: 'Pending', value: tally.pending, color: CHART_COLORS.warning },
      { label: 'Overdue', value: tally.overdue, color: CHART_COLORS.danger  },
    ];
  }, [invoices]);

  const hoursSeries = useMemo(() => {
    const buckets = weekBuckets(8);
    timesheets.forEach((ts) => {
      const startDate = ts.periodStart;
      if (!startDate) return;
      // Snap each ts.periodStart to its Sunday and sum totalHours.
      const d = new Date(startDate + 'T00:00:00Z');
      d.setUTCDate(d.getUTCDate() - d.getUTCDay());
      const key = d.toISOString().slice(0, 10);
      const b = buckets.find(b => b.key === key);
      if (b) b.value += Number(ts.totalHours) || 0;
    });
    return buckets;
  }, [timesheets]);

  const topClients = useMemo(() => {
    const byClient = new Map();
    invoices.forEach((inv) => {
      const name = inv.clientName || `Client #${inv.clientId || '—'}`;
      const cur = byClient.get(name) || { name, count: 0, amount: 0 };
      cur.count += 1;
      cur.amount += Number(inv.amount) || 0;
      byClient.set(name, cur);
    });
    return Array.from(byClient.values())
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }, [invoices]);

  const recentActivity = useMemo(() => {
    const events = [];
    invoices.forEach((inv) => {
      if (inv.issueDate) {
        events.push({
          ts: inv.issueDate,
          icon: Icons.invoice,
          color: ACCENTS.invoice,
          title: `Invoice ${inv.invoiceNumber || `#${inv.id}`} issued`,
          detail: `${inv.clientName || 'Client'} · ${fmtMoney(Number(inv.amount) || 0)}`,
        });
      }
    });
    timesheets.forEach((ts) => {
      if (ts.submittedAt) {
        events.push({
          ts: ts.submittedAt.slice(0, 10),
          icon: Icons.timesheet,
          color: ACCENTS.timesheet,
          title: `${ts.employeeName || 'Employee'} submitted timesheet`,
          detail: `${ts.totalHours || 0}h · ${ts.status || 'pending'}`,
        });
      }
    });
    return events
      .sort((a, b) => (a.ts < b.ts ? 1 : -1))
      .slice(0, 8);
  }, [invoices, timesheets]);

  const totalRevenue = useMemo(
    () => invoices.reduce((sum, i) => sum + (Number(i.amount) || 0), 0),
    [invoices]
  );

  return (
    <div style={{ padding: '24px 32px 40px' }}>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>

      {/* Editorial header — serif welcome, no card chrome */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        gap: '24px', marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: '1px solid var(--hairline)',
      }}>
        <div>
          <div style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '11px', color: 'var(--muted)',
            letterSpacing: '1.4px', textTransform: 'uppercase',
            marginBottom: '6px',
          }}>{today}</div>
          <h1 style={{
            margin: 0,
            fontFamily: 'var(--font-serif)',
            fontSize: '32px', fontWeight: 500,
            color: 'var(--ink)', letterSpacing: '-0.6px',
            lineHeight: 1.15,
          }}>
            Welcome back, {username}
          </h1>
        </div>
        <div style={{
          padding: '5px 12px',
          borderRadius: 'var(--radius-pill)',
          background: roleMeta.bg,
          border: `1px solid ${roleMeta.border}`,
          fontFamily: 'var(--font-sans)',
          fontSize: '11px', fontWeight: 500, color: roleMeta.color,
          letterSpacing: '1.4px', textTransform: 'uppercase',
          whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          {roleMeta.label}
        </div>
      </div>

      {/* Stats */}
      <SectionLabel text="Overview" />
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>

        {isSuper && <>
          <StatCard label="Corporations" value={v('corps')}     accentColor={ACCENTS.corp}     icon={Icons.corp}      onClick={() => navigate('/on/corps')} />
          <StatCard label="Clients"      value={v('clients')}   accentColor={ACCENTS.client}   icon={Icons.client}    onClick={() => navigate('/on/clients')} />
          <StatCard label="Employees"    value={v('employees')} accentColor={ACCENTS.employee} icon={Icons.employee}  onClick={() => navigate('/on/employees')} />
          <StatCard label="Admins"       value={v('admins')}    accentColor={ACCENTS.admin}    icon={Icons.admin}     onClick={() => navigate('/on/admin')} />
        </>}

        {!isSuper && isAdmin && <>
          <StatCard label="Clients"    value={v('clients')}    accentColor={ACCENTS.client}    icon={Icons.client}    onClick={() => navigate('/on/clients')} />
          <StatCard label="Employees"  value={v('employees')}  accentColor={ACCENTS.employee}  icon={Icons.employee}  onClick={() => navigate('/on/employees')} />
          <StatCard label="Timesheets" value={v('timesheets')} accentColor={ACCENTS.timesheet} icon={Icons.timesheet} onClick={() => navigate('/on/timesheets')} />
          <StatCard label="Invoices"   value={v('invoices')}   accentColor={ACCENTS.invoice}   icon={Icons.invoice}   onClick={() => navigate('/on/invoices')} />
          <StatCard label="Orders"   value={v('orders')}   accentColor={ACCENTS.order}   icon={Icons.order}   onClick={() => navigate('/on/orders')} />
        </>}

        {!isSuper && !isAdmin && isEmployer && <>
          <StatCard label="Employees"        value={v('employees')}       accentColor={ACCENTS.employee}  icon={Icons.employee}  onClick={() => navigate('/on/employees')} />
          <StatCard label="Total Invoices"   value={v('invoices')}        accentColor={ACCENTS.invoice}   icon={Icons.invoice}   onClick={() => navigate('/on/invoices')} />
          <StatCard label="Pending Invoices" value={v('pendingInvoices')} accentColor={ACCENTS.pending}   icon={Icons.clock}     />
          <StatCard label="Timesheets"       value={v('timesheets')}      accentColor={ACCENTS.timesheet} icon={Icons.timesheet} onClick={() => navigate('/on/timesheets')} />
        </>}

        {!isSuper && !isAdmin && !isEmployer && isEmployee && <>
          <StatCard label="My Timesheets" value={v('timesheets')}         accentColor={ACCENTS.timesheet} icon={Icons.timesheet} onClick={() => navigate('/on/timesheets')} />
          <StatCard label="Pending"       value={v('pendingTimesheets')}  accentColor={ACCENTS.pending}   icon={Icons.clock} />
          <StatCard label="Approved"      value={v('approvedTimesheets')} accentColor={ACCENTS.approved}  icon={Icons.check} />
        </>}
      </div>

      {/* ── Insights row: big chart 2/3 + compact donut 1/3 ─ */}
      {(isSuper || isAdmin || isEmployer) && invoices.length > 0 && (
        <>
          <SectionLabel text="Insights" />
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(260px, 1fr)',
            gap: '12px',
            marginBottom: '12px',
          }}>
            <Card
              title="Revenue trend"
              subtitle={`Last 6 months · ${fmtMoney(totalRevenue)} total invoiced`}
            >
              <AreaChart data={revenueSeries} formatY={fmtMoney} />
            </Card>
            <Card title="Invoice status" subtitle={`${invoices.length} invoices`}>
              <Donut
                slices={invoiceStatusSlices}
                total={invoices.length}
                centerLabel="Total"
              />
            </Card>
          </div>
        </>
      )}

      {/* ── Activity row: weekly hours 2/3 + top clients 1/3 ─ */}
      {(timesheets.length > 0 || ((isSuper || isAdmin) && topClients.length > 0)) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns:
            timesheets.length > 0 && (isSuper || isAdmin) && topClients.length > 0
              ? 'minmax(0, 2fr) minmax(260px, 1fr)'
              : 'minmax(0, 1fr)',
          gap: '12px',
          marginBottom: '12px',
        }}>
          {timesheets.length > 0 && (
            <Card title="Weekly hours" subtitle="Logged across the last 8 weeks">
              <BarChart data={hoursSeries} formatY={(v) => `${v}h`} />
            </Card>
          )}
          {(isSuper || isAdmin) && topClients.length > 0 && (
            <Card title="Top clients" subtitle="By invoiced amount">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {(() => {
                  const max = Math.max(...topClients.map(v => v.amount), 1);
                  return topClients.map((tv, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                        <span style={{
                          fontFamily: 'var(--font-sans)', fontSize: '13px',
                          color: 'var(--ink)', fontWeight: 500,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{tv.name}</span>
                        <span style={{
                          fontFamily: 'var(--font-sans)', fontSize: '13px',
                          color: 'var(--ink)', fontWeight: 500, flexShrink: 0,
                        }}>{fmtMoney(tv.amount)}</span>
                      </div>
                      <div style={{ height: '4px', background: 'var(--surface-soft)', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${(tv.amount / max) * 100}%`,
                          background: 'var(--coral)',
                          borderRadius: '2px',
                        }} />
                      </div>
                      <div style={{
                        fontFamily: 'var(--font-sans)', fontSize: '11px',
                        color: 'var(--muted-soft)',
                      }}>
                        {tv.count} invoice{tv.count === 1 ? '' : 's'}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ── Recent activity: compact two-column rows ─────── */}
      {recentActivity.length > 0 && (
        <Card title="Recent activity" subtitle={`Last ${recentActivity.length} events`}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            columnGap: '24px',
            rowGap: '2px',
          }}>
            {recentActivity.map((e, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '8px 0',
                borderBottom: '1px solid var(--hairline-soft)',
              }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: 'var(--radius-md)',
                  background: 'var(--surface-soft)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: e.color, flexShrink: 0,
                }}>
                  <span style={{ transform: 'scale(0.75)', display: 'flex' }}>{e.icon}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'var(--font-sans)', fontSize: '13px',
                    color: 'var(--ink)', fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{e.title}</div>
                  <div style={{
                    fontFamily: 'var(--font-sans)', fontSize: '12px',
                    color: 'var(--muted)', marginTop: '1px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{e.detail}</div>
                </div>
                <div style={{
                  fontFamily: 'var(--font-sans)', fontSize: '11px',
                  color: 'var(--muted-soft)', letterSpacing: '0.3px', flexShrink: 0,
                }}>
                  {new Date(e.ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      <div style={{ height: '12px' }} />

      {/* Quick Actions */}
      <SectionLabel text="Quick Actions" />
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {isSuper && <>
          <ActionBtn label="New Corporation" path="/on/corps/new"     color={ACCENTS.corp}     icon={Icons.plus} />
          <ActionBtn label="New Client"      path="/on/clients/new"   color={ACCENTS.client}   icon={Icons.plus} />
          <ActionBtn label="New Employee"    path="/on/employees/new" color={ACCENTS.employee} icon={Icons.plus} />
          <ActionBtn label="New Invoice"     path="/on/invoices/new"  color={ACCENTS.invoice}  icon={Icons.plus} />
        </>}

        {!isSuper && isAdmin && <>
          <ActionBtn label="New Client"   path="/on/clients/new"   color={ACCENTS.client}   icon={Icons.plus} />
          <ActionBtn label="New Employee" path="/on/employees/new" color={ACCENTS.employee} icon={Icons.plus} />
          <ActionBtn label="New Invoice"  path="/on/invoices/new"  color={ACCENTS.invoice}  icon={Icons.plus} />
          <ActionBtn label="New Order"  path="/on/orders/new"  color={ACCENTS.order}  icon={Icons.plus} />
        </>}

        {!isSuper && !isAdmin && isEmployer && <>
          <ActionBtn label="New Employee"  path="/on/employees/new"   color={ACCENTS.employee}  icon={Icons.plus} />
          <ActionBtn label="New Invoice"   path="/on/invoices/new"    color={ACCENTS.invoice}   icon={Icons.plus} />
          <ActionBtn label="New Timesheet" path="/on/timesheets/new"  color={ACCENTS.timesheet} icon={Icons.plus} />
        </>}

        {!isSuper && !isAdmin && !isEmployer && isEmployee && <>
          <ActionBtn label="New Timesheet"        path="/on/timesheets/new"   color={ACCENTS.timesheet} icon={Icons.plus} />
          <ActionBtn label="View Timesheet Items" path="/on/timesheet-items"  color={ACCENTS.employee}  icon={Icons.timesheet} />
        </>}
      </div>
    </div>
  );
};

export default Dashboard;
